<template>
  <MarketingLayout>
    <div class="contact-layout">
      <div class="contact-copy">
        <p class="eyebrow">GET IN TOUCH</p>
        <h1>Let's <em>talk.</em></h1>
        <p class="lead">Questions about WinCard, help with your account, or ready to see a demo? Send us a message and a real person will get back to you.</p>
        <div class="contact-links">
          <router-link to="/help-center">Visit the Help Center <span>→</span></router-link>
          <router-link to="/guides">Browse our guides <span>→</span></router-link>
        </div>
      </div>

      <form class="contact-form-card" @submit.prevent="submitContact">
        <template v-if="!submitted">
          <label for="contact-email">Work email</label>
          <input id="contact-email" v-model="email" type="email" required placeholder="you@business.com" :disabled="submitting" />
          <label for="contact-phone">Phone <span>(optional)</span></label>
          <input id="contact-phone" v-model="phone" type="tel" placeholder="(555) 555-5555" :disabled="submitting" />
          <label for="contact-message">Message</label>
          <textarea id="contact-message" v-model="message" rows="5" required placeholder="How can we help?" :disabled="submitting"></textarea>
          <button class="button" type="submit" :disabled="submitting">{{ submitting ? 'Sending…' : 'Send message' }} <span>→</span></button>
          <small v-if="error" class="form-error">{{ error }}</small>
          <small v-else>We typically reply within one business day.</small>
        </template>
        <div v-else class="contact-success">
          <span>✓</span>
          <h3>Message sent.</h3>
          <p>Thanks for reaching out, we'll get back to you shortly.</p>
        </div>
      </form>

      <div ref="turnstileContainer" class="turnstile-container"></div>
    </div>
  </MarketingLayout>
</template>

<script>
import MarketingLayout from '@/components/MarketingLayout.vue'
import { useSeo } from '@/composables/useSeo'

export default {
  name: 'ContactPage',
  components: { MarketingLayout },
  setup() {
    useSeo({
      title: 'Contact',
      description: 'Get in touch with the WinCard team, questions, demos, or help getting your digital loyalty card live.',
      path: '/contact',
    });
  },
  data() {
    return { email: '', phone: '', message: '', submitting: false, submitted: false, error: '', turnstileWidgetId: null }
  },
  mounted() {
    this.initTurnstile()
  },
  beforeUnmount() {
    window.removeEventListener('turnstile-loaded', this.renderTurnstile)
  },
  methods: {
    initTurnstile() {
      if (!process.env.VUE_APP_TURNSTILE_SITE_KEY) return
      if (window.turnstile) this.renderTurnstile()
      else window.addEventListener('turnstile-loaded', this.renderTurnstile)
    },
    renderTurnstile() {
      if (this.turnstileWidgetId !== null) return
      this.turnstileWidgetId = window.turnstile.render(this.$refs.turnstileContainer, {
        sitekey: process.env.VUE_APP_TURNSTILE_SITE_KEY,
        appearance: 'interaction-only',
        execution: 'execute',
        callback: (token) => { if (this._turnstileResolve) this._turnstileResolve(token) },
        'error-callback': () => { if (this._turnstileReject) this._turnstileReject(new Error('Captcha failed')) },
      })
    },
    getTurnstileToken() {
      if (this.turnstileWidgetId === null) return Promise.resolve(null)
      return new Promise((resolve, reject) => {
        this._turnstileResolve = resolve
        this._turnstileReject = reject
        window.turnstile.reset(this.turnstileWidgetId)
        window.turnstile.execute(this.turnstileWidgetId)
      })
    },
    async submitContact() {
      this.error = ''
      this.submitting = true
      try {
        const cfTurnstileResponse = await this.getTurnstileToken()
        await this.$axios.post('/api/leads', {
          email: this.email,
          phone: this.phone || null,
          message: this.message,
          source_channel: 'website-contact',
          cf_turnstile_response: cfTurnstileResponse,
        })
        this.submitted = true
      } catch (e) {
        this.error = 'Something went wrong. Please try again.'
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style scoped>
.turnstile-container { display: flex; justify-content: center; margin-top: 12px; }
.contact-layout { display: grid; grid-template-columns: 1fr 420px; gap: 70px; align-items: start; }
.contact-copy h1 { font-size: 52px; letter-spacing: -2.6px; line-height: .98; margin: 0 0 18px; font-weight: 600; }
.contact-copy h1 em { font-family: var(--serif); font-weight: 400; }
.contact-copy .lead { max-width: 460px; }
.contact-links { display: flex; flex-direction: column; gap: 12px; margin-top: 30px; }
.contact-links a { font-size: 13px; font-weight: 600; color: var(--green); display: inline-flex; align-items: center; gap: 7px; }

.contact-form-card { background: var(--cream); border: 1px solid #e2e2e5; border-radius: 12px; padding: 31px; }
.contact-form-card label { display: block; font-size: 11px; font-weight: 600; margin-bottom: 7px; }
.contact-form-card label span { font-weight: 400; color: #85858b; }
.contact-form-card input, .contact-form-card textarea { display: block; width: 100%; border: 1px solid #d5d5db; background: #fff; border-radius: 7px; padding: 13px 12px; font: 13px var(--sans); color: var(--ink); margin-bottom: 14px; outline: none; resize: vertical; }
.contact-form-card input:focus, .contact-form-card textarea:focus { border-color: var(--green); box-shadow: 0 0 0 3px #e9e5ff; }
.contact-form-card .button { width: 100%; border: 0; justify-content: space-between; cursor: pointer; }
.contact-form-card .button:disabled { opacity: .6; cursor: not-allowed; transform: none; }
.contact-form-card > small { display: block; color: #85858b; line-height: 1.45; font-size: 9px; margin-top: 13px; }
.form-error { color: #c1443d !important; }

.contact-success { min-height: 220px; display: flex; flex-direction: column; justify-content: center; }
.contact-success > span { width: 32px; height: 32px; display: grid; place-items: center; border-radius: 50%; background: var(--lime); color: var(--ink); margin-bottom: 13px; }
.contact-success h3 { margin: 0 0 6px; }
.contact-success p { font-size: 13px; line-height: 1.55; color: var(--muted); margin: 0; }

@media (max-width: 900px) { .contact-layout { grid-template-columns: 1fr; gap: 40px; } }
@media (max-width: 650px) { .contact-copy h1 { font-size: 40px; } }
</style>
